'use client';

import Link from 'next/link';
import type { CTASectionProps } from '@routeroyal/theme-core';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

/**
 * Route Royal CTA Section
 *
 * Clean, professional call-to-action matching the Route Royal brand.
 */
export function RouteRoyalCTASection({
  title = 'Ready to get started?',
  description = 'Get in touch with our team to discuss your requirements.',
  primaryButton = { text: 'Contact Us', href: '/contact' },
  secondaryButton,
  layout = 'centered',
}: CTASectionProps) {
  return (
    <section className="py-20 lg:py-28 bg-[#1E3A8A] text-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tight mb-4">
          {title}
        </h2>

        {description && (
          <p className="text-lg lg:text-xl text-blue-200 mb-10 max-w-2xl mx-auto">
            {description}
          </p>
        )}

        <div
          className={`flex flex-col sm:flex-row gap-4 ${
            layout === 'centered' ? 'justify-center' : 'justify-start'
          }`}
        >
          {primaryButton && (
            <Link
              href={primaryButton.href}
              target={primaryButton.external ? '_blank' : undefined}
              rel={primaryButton.external ? 'noopener noreferrer' : undefined}
              onClick={() =>
                analyticsEvents.custom('cta_click', {
                  cta_text: primaryButton.text,
                  cta_location: 'cta_section',
                  cta_destination: primaryButton.href,
                  cta_variant: 'primary',
                })
              }
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#1E3A8A] font-semibold rounded-lg hover:bg-blue-50 transition-colors"
            >
              {primaryButton.text}
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          )}

          {secondaryButton && (
            <Link
              href={secondaryButton.href}
              target={secondaryButton.external ? '_blank' : undefined}
              rel={secondaryButton.external ? 'noopener noreferrer' : undefined}
              onClick={() =>
                analyticsEvents.custom('cta_click', {
                  cta_text: secondaryButton.text,
                  cta_location: 'cta_section',
                  cta_destination: secondaryButton.href,
                  cta_variant: 'secondary',
                })
              }
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/40 text-white font-semibold rounded-lg hover:border-white hover:bg-white/10 transition-colors"
            >
              {secondaryButton.text}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
